// World convention (PLAN §5.7.2): 1 unit = 1 board square, board on XZ, +Y up
// (glTF native, so no axis conversion at load), file -> +X, rank -> -Z, White
// at the near edge. Both asset packs are already authored at this scale, so
// there is never a per-asset scale factor — fix bad art in tools/buildAssets.mjs.

import { Vector3 } from "three";

import { COORD_MAX, COORD_MIN, fileOf, rankOf, square } from "src/shared/board";

export const SQUARE_SIZE = 1;
export const BOARD_SIZE = 8;

/** Half-extent of the board, so the centre of the board sits at the origin. */
const OFFSET = (BOARD_SIZE - 1) / 2;

export const fileToX = (file: number): number => file - OFFSET;
export const rankToZ = (rank: number): number => OFFSET - rank;

export function squareToWorld(sq: number, y = 0, target = new Vector3()): Vector3 {
  return target.set(fileToX(fileOf(sq)), y, rankToZ(rankOf(sq)));
}

/**
 * Inverse of squareToWorld.
 *
 * Returns whatever square the point falls in, on or off the board — which
 * squares *exist* is a property of the position, not of the coordinate system,
 * and the caller checks. It used to reject anything outside files and ranks
 * 0..7, which meant that the moment the board grew you could not click on any
 * of the new ground.
 */
export function worldToSquare(x: number, z: number): number {
  const file = Math.round(x + OFFSET);
  const rank = Math.round(OFFSET - z);
  if (file < COORD_MIN || file > COORD_MAX || rank < COORD_MIN || rank > COORD_MAX) {
    return -1;
  }
  return square(file, rank);
}

/**
 * Facing (Phase 3) as a Y rotation, for markers whose geometry points along
 * local -Z. North is -Z, matching rank -> -Z: a White pawn advancing up the
 * ranks moves toward -Z, which is yaw 0.
 *
 * East and West are NEGATIVE and POSITIVE respectively, which is easy to get
 * backwards — it shipped backwards from Phase 3 until a playtest caught it.
 * Three's Y rotation sends a local -Z vector to (z*sin0, 0, z*cos0), so at
 * +PI/2 the tip of a north-pointing marker lands on -X, which is *West*. East
 * is +X, so East needs -PI/2.
 *
 * Everything directional reads this: pawn chevrons, belt parts, the in-world
 * direction picker and minted-pawn facing. Getting it wrong rotates the whole
 * visual language away from the rules while every self-consistent test still
 * passes, so `coords.test.ts` checks it against DIR_OFFSET and the world axes
 * rather than against itself.
 */
export const FACING_YAW = [0, -Math.PI / 2, Math.PI, Math.PI / 2] as const;
